import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import theme from "../../../../theme";
import { Loader } from "../../../../components/Loader";
import { CardLine } from "../../../IndividualView";
import { useProfileMandates } from "../../../../data/useProfileMandates";
import { Flex } from "../../../../components/Flex";
import { Paragraph } from "../../../../components/Paragraph";
import { DateTime } from "luxon";
import { Box } from "../../../../components/Box";
import { useFillViewportHeight } from "../../../../hooks/useFillViewportHeight";
import {
  MandateStatus,
  useMandateStatuses,
} from "../../../../context/MandateStatusContext";
import { MandateStatusColours } from "../../../../components/layoutConfig";
import { Button } from "../../../../components/Button";
import { Profile } from "../../../../types/Profile";

export const ApplicationsTab = ({
  profile: profileOverride,
}: {
  profile?: Profile;
}) => {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight();
  const outlet = useOutletContext<{ profile: Profile } | null>();
  const profile = profileOverride ?? outlet?.profile;
  const { data, isLoading, isError } = useProfileMandates(profile?.id);
  const { statuses, isLoadingStatuses } = useMandateStatuses();

  const mandateStatusById = useMemo(() => {
    const list =
      statuses?.data && Array.isArray(statuses.data) ? statuses.data : [];

    return list.reduce((acc: Record<number, MandateStatus>, item: any) => {
      acc[item.id] = item.status as MandateStatus;
      return acc;
    }, {});
  }, [statuses]);

  if (isLoading || isLoadingStatuses) return <Loader />;
  if (isError) return <CardLine>Failed to load applications.</CardLine>;

  const applications = data?.results ?? [];

  const getApplicationViewUrl = (app: any) => {
    return `/applications/${app.id}`;
  };

  return (
    <Box ref={fillRef} height={fillHeight} overflowY="auto" overflowX="hidden">
      {applications.length ? (
        <Box
          background={theme.colors.white}
          borderRadius={12}
          border={`1px solid ${theme.colors.divider}`}
          overflow="hidden"
        >
          {/* Header row */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            p={14}
            borderBottom={`1px solid ${theme.colors.divider}`}
            background="#F9FAFB"
          >
            <Paragraph my={0} fontWeight={600} width="26%" color="gray.60">
              Name
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="22%" color="gray.60">
              Reference
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="18%" color="gray.60">
              Status
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="18%" color="gray.60">
              Created
            </Paragraph>
            <Box width="16%" />
          </Flex>

          {/* Data rows */}
          {applications.map((app: any) => {
            const createdOn = app.created_on
              ? DateTime.fromISO(app.created_on).toFormat("dd/MM/yyyy")
              : "-";

            const mandateStatus = mandateStatusById[app.status];

            const statusLabel = mandateStatus
              ? mandateStatus.replace(/\b\w/g, (c) => c.toUpperCase())
              : "-";

            const statusColor = mandateStatus
              ? MandateStatusColours[mandateStatus]
              : "gray.60";

            return (
              <Flex
                key={app.id}
                justifyContent="space-between"
                alignItems="center"
                p={14}
                borderBottom={`1px solid ${theme.colors.divider}`}
              >
                <Paragraph fontWeight={600} my={0} width="26%">
                  {app.name ?? "-"}
                </Paragraph>

                <Paragraph my={0} width="22%" color="gray.60">
                  {app.reference ?? "-"}
                </Paragraph>

                <Paragraph
                  my={0}
                  width="18%"
                  color={statusColor}
                  fontWeight={500}
                >
                  {statusLabel}
                </Paragraph>

                <Paragraph my={0} width="18%" color="gray.60">
                  {createdOn}
                </Paragraph>

                <Box
                  width="16%"
                  display="flex"
                  justifyContent="flex-end"
                >
                  <Button
                    variant="primary"
                    onClick={() =>
                      window.open(getApplicationViewUrl(app), "_blank")
                    }
                  >
                    View
                  </Button>
                </Box>
              </Flex>
            );
          })}
        </Box>
      ) : (
        <CardLine>No applications found.</CardLine>
      )}
    </Box>
  );
};
